import { useCallback, useEffect, useRef } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re/portal/resident/welcome`;

/** Counts only narration actually heard (no seeking, not muted, page visible) and saves it about every 10s, on pause and on close. */
export function useTourTracking({ token, d, audio, step }) {
  const played = useRef({});
  const last = useRef(0);
  const dirty = useRef(false);
  const pos = useRef(null);
  const on = !!d?.tracking;
  useEffect(() => { if (d) played.current = { ...(d.video?.played || {}) }; }, [d]);
  const save = useCallback((beacon = false) => {
    if (!on || !dirty.current) return;
    dirty.current = false;
    const body = JSON.stringify({ played: played.current, position: pos.current || {} });
    fetch(`${API}/progress?token=${encodeURIComponent(token)}`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: beacon })
      .catch(() => { dirty.current = true; });
  }, [on, token]);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => save(), 10000);
    const hide = () => { if (document.visibilityState === "hidden") save(true); };
    const leave = () => save(true);
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("pagehide", leave);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", hide); window.removeEventListener("pagehide", leave); save(true); };
  }, [on, save]);
  const onTime = () => {
    const a = audio.current;
    if (!a || step < 0) return;
    const t = a.currentTime;
    const dt = t - last.current;
    last.current = t;
    pos.current = { step, time: Math.round(t * 10) / 10 };
    if (dt > 0 && dt < 1.5 && !a.paused && !a.muted && a.volume > 0 && document.visibilityState === "visible") {
      played.current[step] = Math.min(a.duration || Infinity, (played.current[step] || 0) + dt);
      dirty.current = true;
    }
  };
  const onSync = () => { last.current = audio.current?.currentTime || 0; };
  return { handlers: { onTimeUpdate: onTime, onSeeking: onSync, onSeeked: onSync, onLoadedMetadata: onSync, onPause: () => { dirty.current = true; save(); } }, save };
}
